import React, { useEffect, useRef, useState } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import moment from 'moment';

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const pad = (n) => String(n).padStart(2, '0');

// Self-built date + time picker matching the inv- design system — native
// <input type="datetime-local"> renders a different (and on some browsers clunky,
// overlapping) calendar+clock popup per platform. This gives one consistent,
// touch-friendly control across desktop and mobile.
const DateTimeField = ({ label, value, onChange, max }) => {
    const [open, setOpen] = useState(false);
    const [viewDate, setViewDate] = useState(() => new Date(value.getFullYear(), value.getMonth(), 1));
    const containerRef = useRef(null);
    const maxDate = max || new Date();

    useEffect(() => {
        if (!open) return;
        setViewDate(new Date(value.getFullYear(), value.getMonth(), 1));
        const handleClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysCount = new Date(year, month + 1, 0).getDate();
    const cells = [...Array(firstWeekday).fill(null), ...Array.from({ length: daysCount }, (_, i) => i + 1)];
    const isNextMonthDisabled = year > maxDate.getFullYear() || (year === maxDate.getFullYear() && month >= maxDate.getMonth());

    const isDayDisabled = (day) => {
        const d = new Date(year, month, day, 23, 59, 59, 999);
        return d > maxDate;
    };

    const selectDay = (day) => {
        const next = new Date(value);
        next.setFullYear(year, month, day);
        onChange(next);
    };

    const hours24 = value.getHours();
    const minutes = value.getMinutes();
    const hour12 = ((hours24 + 11) % 12) + 1;
    const isPM = hours24 >= 12;

    const setTime = ({ hour12: h = hour12, minutes: m = minutes, isPM: pm = isPM }) => {
        let h24 = h % 12;
        if (pm) h24 += 12;
        const next = new Date(value);
        next.setHours(h24, m, 0, 0);
        onChange(next);
    };

    const selectClasses = 'rounded-lg border border-inv-cream-200 bg-white px-2 py-2 text-sm text-inv-ink focus:border-inv-brand focus:outline-none';

    return (
        <div ref={containerRef} className="relative">
            <label className="mb-1 block text-xs font-medium text-[#6B675F]">{label}</label>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center gap-2 rounded-lg border border-inv-cream-200 bg-white px-3 py-2.5 text-left text-sm text-inv-ink hover:border-inv-brand"
            >
                <CalendarIcon size={15} className="shrink-0 text-[#8C8175]" />
                {moment(value).format('DD MMM YYYY, h:mm A')}
            </button>

            {open && (
                <div className="absolute z-50 mt-1 w-72 rounded-xl border border-inv-cream-200 bg-white p-3 shadow-lg">
                    <div className="mb-2 flex items-center justify-between">
                        <button
                            type="button"
                            onClick={() => setViewDate(new Date(year, month - 1, 1))}
                            className="rounded-lg p-1 text-[#6B675F] hover:bg-inv-cream-100"
                        >
                            <ChevronLeftIcon size={16} />
                        </button>
                        <span className="text-sm font-semibold text-inv-ink">{moment(viewDate).format('MMMM YYYY')}</span>
                        <button
                            type="button"
                            onClick={() => setViewDate(new Date(year, month + 1, 1))}
                            disabled={isNextMonthDisabled}
                            className="rounded-lg p-1 text-[#6B675F] hover:bg-inv-cream-100 disabled:opacity-30 disabled:hover:bg-transparent"
                        >
                            <ChevronRightIcon size={16} />
                        </button>
                    </div>

                    <div className="grid grid-cols-7 gap-y-1 text-center text-[11px] font-semibold text-[#8C8175]">
                        {WEEKDAY_LABELS.map((d, i) => <div key={i}>{d}</div>)}
                    </div>
                    <div className="mt-1 grid grid-cols-7 gap-y-1">
                        {cells.map((day, i) => {
                            if (!day) return <div key={i} />;
                            const disabled = isDayDisabled(day);
                            const selected = isSameDay(new Date(year, month, day), value);
                            return (
                                <button
                                    type="button"
                                    key={i}
                                    disabled={disabled}
                                    onClick={() => selectDay(day)}
                                    className={`mx-auto flex h-7 w-7 items-center justify-center rounded-lg text-xs font-medium ${
                                        selected ? 'bg-inv-brand text-white' : disabled ? 'cursor-not-allowed text-[#d8d3c8]' : 'text-inv-ink hover:bg-inv-cream-100'
                                    }`}
                                >
                                    {day}
                                </button>
                            );
                        })}
                    </div>

                    <div className="mt-3 border-t border-inv-cream-100 pt-3">
                        <label className="mb-1 block text-xs font-medium text-[#6B675F]">Time</label>
                        <div className="grid grid-cols-3 gap-2">
                            <select
                                value={hour12}
                                onChange={(e) => setTime({ hour12: Number(e.target.value) })}
                                className={selectClasses}
                            >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                                    <option key={h} value={h}>{pad(h)}</option>
                                ))}
                            </select>
                            <select
                                value={minutes}
                                onChange={(e) => setTime({ minutes: Number(e.target.value) })}
                                className={selectClasses}
                            >
                                {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                                    <option key={m} value={m}>{pad(m)}</option>
                                ))}
                            </select>
                            <select
                                value={isPM ? 'PM' : 'AM'}
                                onChange={(e) => setTime({ isPM: e.target.value === 'PM' })}
                                className={selectClasses}
                            >
                                <option value="AM">AM</option>
                                <option value="PM">PM</option>
                            </select>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="mt-3 w-full rounded-lg bg-inv-brand py-1.5 text-sm font-semibold text-white hover:bg-inv-brand-dark"
                    >
                        Done
                    </button>
                </div>
            )}
        </div>
    );
};

export default DateTimeField;
